import { useEffect } from 'react';
import { Button, IconButton, Stack, Text, ToggleTip } from '@figma/fpl-components';
import { Icon24Link, Icon24LogoWeavy, Icon24NewTab, Icon24SidebarOpen } from '@figma/fpl-icons';
import cssVars from '@figma/fpl-tokens/css-vars';
import { color, spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { I18N_KEYS } from '@/language/keys';
import { sidebarVisibilityStyles } from './ToolLayout.stylex';
import type { ReactNode } from 'react';

export type ToolViewTitleBarProps =
  | { variant: 'logo-only'; onBackToDashboard: () => void }
  | {
      variant?: 'tool';
      toolId?: string;
      name: string;
      isSidebarOpen: boolean;
      onToggleSidebar: () => void;
      hideSidebarOnMobile?: boolean;
      onOpenWorkflow?: () => void;
      /** Published `/tool` routes return to Tools. The in-workflow preview leaves this unset. */
      onBackToDashboard?: () => void;
      showShare?: boolean;
      shareDisabledTooltip?: boolean;
      onShare?: () => void;
      credits?: ReactNode;
    };

export function ToolViewTitleBar(props: ToolViewTitleBarProps) {
  if (props.variant === 'logo-only') {
    return <HeaderLayout onBackToDashboard={props.onBackToDashboard} />;
  }

  return <ToolHeaderWithActions {...props} />;
}

function ToolHeaderWithActions(props: Extract<ToolViewTitleBarProps, { variant?: 'tool' }>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { copied: isLinkCopied, copyToClipboard } = useCopyToClipboard({
    trackingName: 'tool_view_copy_link',
  });
  const sidebarLabel = t(props.isSidebarOpen ? I18N_KEYS.TOOL_VIEW.HIDE_SIDEBAR : I18N_KEYS.TOOL_VIEW.SHOW_SIDEBAR);
  const sidebarVisibilityStyle = props.hideSidebarOnMobile
    ? sidebarVisibilityStyles.desktopOnly
    : sidebarVisibilityStyles.alwaysVisible;
  const shareTooltipManager = ToggleTip.useUncontrolledToggleTip({ allowOnDisabled: true, provideOwnClick: true });

  useEffect(() => {
    document.title = props.name;
    return () => {
      document.title = t(I18N_KEYS.GENERAL.PAGE_TITLE);
    };
  }, [props.name, t]);

  const actions = (
    <Stack.Row {...stylex.props(styles.actions)} gap={cssVars.spacer.x8px}>
      {props.credits}
      {props.onOpenWorkflow && (
        <Button variant="secondary" size="lg" onClick={props.onOpenWorkflow}>
          <span {...stylex.props(styles.workflowLabel)}>
            {t(I18N_KEYS.TOOL_VIEW.OPEN_WORKFLOW)}
            <Button.Icon>
              <Icon24NewTab />
            </Button.Icon>
          </span>
        </Button>
      )}
      {props.showShare ? (
        <>
          <Button
            {...(props.shareDisabledTooltip ? shareTooltipManager.getTriggerProps() : { onClick: props.onShare })}
            variant="primary"
            size="lg"
            disabled={!!props.shareDisabledTooltip}
          >
            {t(I18N_KEYS.RECIPE_MAIN.FLOW.SHARE)}
          </Button>
          {props.shareDisabledTooltip && (
            <ToggleTip.Container manager={shareTooltipManager} role="tooltip">
              <ToggleTip.Content>{t(I18N_KEYS.TOOL_SHARE.PUBLISH_FIRST)}</ToggleTip.Content>
            </ToggleTip.Container>
          )}
        </>
      ) : props.toolId ? (
        <Button
          variant="primary"
          size="lg"
          iconPrefix={<Icon24Link />}
          onClick={() => void copyToClipboard(`${window.location.origin}/tool/${props.toolId}`)}
        >
          {t(isLinkCopied ? I18N_KEYS.GENERAL.COPIED : I18N_KEYS.GENERAL.COPY_LINK)}
        </Button>
      ) : null}
    </Stack.Row>
  );

  return (
    <HeaderLayout onBackToDashboard={props.onBackToDashboard ?? (() => navigate('/'))} actions={actions}>
      <Text.span
        title={props.name || t(I18N_KEYS.TOOL_VIEW.UNTITLED)}
        variant="body-large-strong"
        truncate
        hideTooltip
        {...stylex.props(styles.title)}
      >
        {props.name || t(I18N_KEYS.TOOL_VIEW.UNTITLED)}
      </Text.span>
      <span {...stylex.props(sidebarVisibilityStyle)}>
        <IconButton
          aria-label={sidebarLabel}
          title={sidebarLabel}
          aria-controls="tool-viewer-sidebar"
          onClick={props.onToggleSidebar}
          size="lg"
          variant="ghost"
        >
          <Icon24SidebarOpen />
        </IconButton>
      </span>
    </HeaderLayout>
  );
}

function HeaderLayout({
  onBackToDashboard,
  children,
  actions,
}: {
  onBackToDashboard: () => void;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <header {...stylex.props(styles.root)}>
      <Stack.Row {...stylex.props(styles.content)} justify="space-between" gap={cssVars.spacer.x16px}>
        <Stack.Row {...stylex.props(styles.titleGroup)} gap={cssVars.spacer.x8px}>
          <IconButton
            aria-label={t(I18N_KEYS.SHARED_ASSET_VIEW.DASHBOARD_ARIA)}
            onClick={onBackToDashboard}
            size="lg"
            variant="ghost"
          >
            <Icon24LogoWeavy />
          </IconButton>
          {children}
        </Stack.Row>
        {actions}
      </Stack.Row>
    </header>
  );
}

const styles = stylex.create({
  root: {
    flexShrink: 0,
    height: 48,
    boxSizing: 'border-box',
    paddingLeft: spacer.spacer8px,
    paddingRight: spacer.spacer16px,
    backgroundColor: color.bg,
    color: color.text,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.border,
  },
  content: {
    height: '100%',
  },
  titleGroup: {
    minWidth: 0,
  },
  title: {
    minWidth: 0,
  },
  actions: {
    flexShrink: 0,
  },
  workflowLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: spacer.spacer4px,
  },
});
